import { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import Toast from "components/cms/Toast";
import { getErrorMessage } from "utils/cms";
import { deleteNewsletterEntry, listNewsletterEntries } from "api/newsletterentry";

const LIMIT = 20;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function NewsletterEntriesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    setError("");
    try {
      const res = await listNewsletterEntries(pageNum, LIMIT);
      const data = res.data?.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.response?.status === 404) {
        setItems([]);
        setTotal(0);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const onDelete = async (item) => {
    if (!window.confirm(`Delete entry “${item.email}”? This cannot be undone.`)) {
      return;
    }
    setDeletingId(item._id);
    try {
      const res = await deleteNewsletterEntry(item._id);
      setToast({
        type: "success",
        message: res.data?.message || "Deleted successfully",
        id: Date.now(),
      });
      if (items.length === 1 && page > 1) {
        setPage((value) => value - 1);
      } else {
        await load(page);
      }
    } catch (err) {
      setToast({ type: "error", message: getErrorMessage(err), id: Date.now() });
    } finally {
      setDeletingId("");
    }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <Card extra="overflow-hidden rounded-2xl border border-gray-200 shadow-none dark:!border-white/10">
        <div className="flex items-center justify-between border-b border-purple-100 bg-gradient-to-r from-[#F3F0FF] to-white px-4 py-3 dark:border-white/10 dark:from-navy-800 dark:to-navy-800">
          <div>
            <h3 className="text-base font-bold text-navy-700 dark:text-white">
              Newsletter entries
            </h3>
            <p className="text-xs text-gray-500">
              {total} email{total === 1 ? "" : "s"} from the newsletter form
            </p>
          </div>
        </div>

        {error ? (
          <p className="px-4 py-6 text-sm text-red-500">{error}</p>
        ) : loading ? (
          <p className="px-4 py-6 text-sm text-gray-500">Loading entries…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500">
            No entries yet. Emails submitted on the website newsletter form will
            appear here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left">
              <thead>
                <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-[0.12em] text-[#6A4CF0] dark:border-white/10">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b border-gray-50 text-sm text-navy-700 last:border-0 dark:border-white/5 dark:text-white"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 font-medium">{item.email || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={deletingId === item._id}
                        onClick={() => onDelete(item)}
                        className="inline-flex rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
                      >
                        {deletingId === item._id ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > LIMIT ? (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm dark:border-white/10">
            <p className="text-gray-500">
              Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-white/10"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((value) => value + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-white/10"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
